import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { X } from "lucide-react";
import { pc, waUrl } from "../lib/api";
import { useData } from "../lib/app";
import { AiLoader, ErrorCard } from "../components/bits";
import { useLang, useT } from "../lib/i18n";

export default function Story() {
  const nav = useNavigate();
  const t = useT();
  const lang = useLang();
  const { data: s, isLoading, error, refetch } = useData(`/story?lang=${encodeURIComponent(lang)}`);
  const [i, setI] = useState(0);
  useEffect(() => {
    if (!s) return undefined;
    const h = setTimeout(() => setI((x) => Math.min(x + 1, 3)), 6000);
    return () => clearTimeout(h);
  }, [i, s]);
  if (isLoading) return <div className="max-w-[480px] mx-auto p-6 pt-24"><AiLoader kind="story" /></div>;
  if (error) return <div className="max-w-[480px] mx-auto p-6 pt-24"><ErrorCard onRetry={refetch} /></div>;
  const shareText = `📖 Our week on UnloadIt: ${s.total} things ran our home. ${s.admin_name} kept ${s.admin_kept}, the family took ${s.family_took}. ${s.admin_name}'s share: ${s.share}%${s.last_share != null ? ` (last week ${s.last_share}%)` : ""} 💜`;
  const cards = [
    <><p className="font-display text-[96px] leading-none font-extrabold" data-testid="story-total">{s.total}</p><p className="font-display text-3xl font-bold mt-2">{t("things ran your home this week")}</p></>,
    <>
      <div className="grid grid-cols-2 gap-3 mb-6">
        <div className="bg-white/15 rounded-2xl p-4"><p className="font-display text-5xl font-extrabold">{s.admin_kept}</p><p>{s.viewer_is_admin ? t("you kept") : t("{name} kept", { name: s.admin_name })}</p></div>
        <div className="bg-white/15 rounded-2xl p-4"><p className="font-display text-5xl font-extrabold">{s.family_took}</p><p>{t("family took")}</p></div>
      </div>
      <div className="flex flex-wrap gap-2">{s.people.map((p) => <span key={p.name} className="rounded-full px-3 py-1.5 font-bold" style={{ background: pc(p.color).tint, color: pc(p.color).text }}>{p.name} {p.count}</span>)}</div>
    </>,
    <>
      <p className="ul-label !text-white/80">{s.viewer_is_admin ? t("Your share") : t("{name}'s share", { name: s.admin_name })}</p>
      <p className="font-display text-[80px] leading-none font-extrabold" data-testid="story-share">{s.share}%</p>
      <div className="h-4 bg-white/20 rounded-full mt-4 overflow-hidden"><motion.div initial={{ width: 0 }} animate={{ width: `${s.share}%` }} transition={{ duration: 1 }} className="h-full bg-white rounded-full" /></div>
      <p className="mt-3 font-semibold">{s.last_share != null && t("last week {n}%", { n: s.last_share })}{s.start_share != null && ` · ${t("when you started {n}%", { n: s.start_share })}`}</p>
    </>,
    <>
      <p className="text-xl leading-relaxed font-semibold" data-testid="story-note">"{s.note}"</p>
      <a data-testid="story-share-wa" href={waUrl(shareText)} target="_blank" rel="noreferrer" className="ul-btn w-full mt-8" style={{ background: "#E7F6EC", color: "#1E7A43" }}>{t("Share on WhatsApp")}</a>
      <p className="text-xs text-white/80 text-center mt-3">{t("✨ Written by AI from your family's real tasks")}</p>
    </>,
  ];
  return (
    <div className="min-h-screen flex justify-center bg-[#2A2540]">
      <div className="w-full max-w-[480px] min-h-screen text-white relative select-none" style={{ background: "linear-gradient(165deg,#6A5AE0 0%,#9B5FD0 55%,#FF6FA3 100%)" }} data-testid="story-screen">
        <div className="flex gap-1.5 p-4 pt-5">{cards.map((_, k) => <div key={k} className="h-1 flex-1 rounded-full bg-white/30 overflow-hidden"><div className="h-full bg-white transition-[width] duration-300" style={{ width: k <= i ? "100%" : "0%" }} /></div>)}</div>
        <div className="flex justify-between items-center px-5">
          <p className="ul-label !text-white/90">{t("Your Unload Story")} · {s.range}</p>
          <button data-testid="story-close" onClick={() => nav("/")} aria-label="close"><X /></button>
        </div>
        <div className="absolute inset-0 top-24 flex">
          <button className="w-1/3 h-full" aria-label="previous" data-testid="story-prev" onClick={() => setI(Math.max(0, i - 1))} />
          <button className="w-2/3 h-full" aria-label="next" data-testid="story-next" onClick={() => setI(Math.min(3, i + 1))} />
        </div>
        <motion.div key={i} initial={{ opacity: 0, x: 30 }} animate={{ opacity: 1, x: 0 }} className="relative px-7 pt-20 pointer-events-none [&_a]:pointer-events-auto">{cards[i]}</motion.div>
      </div>
    </div>
  );
}
